import { type FormEvent, useMemo } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import {
	useContributionsPageData,
	useContributionsPageState,
} from '#src/app/pages/accountSettings/contributions/main/pageContext';

import { useSubmit } from './hooks/useSubmit';

type Result = ModelHookResult<
	{
		title: string;
		isDisabled: boolean;
		loading: boolean;
		formError?: string | null;
		buttonLabel: string;
	},
	{
		submit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
	}
>;

export function useChangeContributionsForm(): Result {
	const { translate } = useI18n();
	const {
		data: {
			featureConfigs: { shouldUseDonationLanguage },
		},
	} = useContributionsPageData();

	const [
		{
			subscription: { unsavedAmount, amount, formState, interval },
		},
	] = useContributionsPageState();

	const submit = useSubmit();

	const i18nSuffix = shouldUseDonationLanguage ? '_donation' : '';

	const { title, buttonLabel } = useMemo(() => {
		return {
			title:
				interval === 'YEAR'
					? translate(`corgi.membership.manage.yearly_amount_header${i18nSuffix}`)
					: translate(`corgi.membership.manage.contribute.monthly_amount_header${i18nSuffix}`),
			buttonLabel: translate(`corgi.membership.manage.change_plan${i18nSuffix}`),
		};
	}, [i18nSuffix, interval, translate]);

	return {
		data: {
			title,
			isDisabled: unsavedAmount === amount,
			loading: formState?.status === 'loading',
			formError: formState?.status === 'error' ? formState.error : undefined,
			buttonLabel,
		},
		actions: {
			submit,
		},
	};
}
